/* eslint-disable no-unused-vars */
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { ReactComponent as MailIcon } from '../../assets/icons/mail.svg'
import EmailsCard from 'components/EmailCard'
import EmailModal from './EmailModal'
import Skeleton from 'react-loading-skeleton'
import { getUserThreads, getUserEmails } from 'store/actions/usersActions'
import { isMobile } from 'react-device-detect'
import { sanitizeCode } from 'utils'

const EmailTab = ({ contact }) => {
  const [showEmailBox, setShowEmailBox] = useState(false)
  const [listOfEmails, setListOfEmails] = useState([])
  const [gettingListOfEmails, setGettingListOfEmails] = useState(false)
  const [selectedEmailIndex, setSelectedEmailIndex] = useState(0)
  const [selectedThreadContent, setSelectedThreadContent] = useState('')
  const [composingEmailSubject, setComposingEmailSubject] = useState('')
  const [showMessageList, setShowMessageList] = useState(isMobile)
  const [selectedThreadId, setSelectedThreadId] = useState('')
  const [gettingThreadContent, setGettingThreadContent] = useState(false)
  const [composingEmailBody, setComposingEmailBody] = useState('')
  const [composingEmailRecipient, setComposingEmailRecipient] = useState([])
  const [searchFilter, setSearchFilter] = useState('')

  const refLocalListOfEmails = useRef(listOfEmails)

  const updateListOfEmails = (newState) => {
    refLocalListOfEmails.current = newState
    setListOfEmails(newState)
  }

  const getEmails = useCallback(() => {
    if (!contact?.id) return
    setGettingListOfEmails(true)
    getUserThreads({ threadId: contact?.id, search: searchFilter })
      .then((newEmails) => {
        const currentEmails = [...newEmails]
        const listOfEmails = currentEmails.map((email) => ({
          lastMessageTimestamp:
            email.last_message_timestamp || email.lastMessageTimestamp,
          messages: email.messages,
          snippet: email.snippet,
          subject: email.subject
        }))

        setGettingListOfEmails(false)
        updateListOfEmails(listOfEmails)
      })
      .catch(() => setGettingListOfEmails(false))
  }, [contact?.id, searchFilter])

  useEffect(() => {
    if (listOfEmails.length) {
      setSelectedEmailIndex(-1)
      setTimeout(() => {
        setSelectedEmailIndex(0)
      }, 500)
    }
  }, [listOfEmails?.length])

  useEffect(() => {
    getEmails()
  }, [getEmails])

  useEffect(() => {
    if (selectedEmailIndex >= 0) {
      const selectedEmailThreadId =
        refLocalListOfEmails.current?.[selectedEmailIndex]?.messages?.[0]
          ?.thread_id
      setSelectedThreadId(selectedEmailThreadId)
    }
  }, [selectedEmailIndex])

  useEffect(() => {
    if (selectedThreadId) {
      setGettingThreadContent(true)
      getUserEmails({ id: selectedThreadId })
        .then((emails) => {
          setSelectedThreadContent({
            subject: emails[0].subject,
            senderName: `${emails[0].from[0].name && emails[0].from[0].name}`,
            senderEmail: emails[0].from[0].email,
            items: emails.map((email, index) => {
              const emailDate = new Date(email.date * 1000)

              return {
                id: index,
                author: email.from[0].name || 'Unknown sender',
                date: `${emailDate.toLocaleDateString('en-US', {
                  weekday: 'long'
                })} at ${emailDate.toLocaleTimeString('en-US', {
                  hour: '2-digit',
                  minute: '2-digit'
                })}`,
                datetime: email.date,
                body: sanitizeCode(email.body),
                files: email.files
              }
            })
          })
          setGettingThreadContent(false)
        })
        .catch(() => setGettingThreadContent(false))
    }
  }, [selectedThreadId])

  const childProps = {
    selectedEmailIndex,
    setSelectedEmailIndex,
    selectedThreadContent,
    setSelectedThreadContent,
    composingEmailSubject,
    setComposingEmailSubject,
    showMessageList,
    setShowMessageList,
    isMobile,
    refLocalListOfEmails,
    gettingListOfEmails,
    getEmails,
    selectedThreadId,
    setSelectedThreadId,
    showEmailBox,
    setShowEmailBox,
    gettingThreadContent,
    setGettingThreadContent,
    composingEmailBody,
    setComposingEmailBody,
    composingEmailRecipient,
    setComposingEmailRecipient,
    listOfEmails,
    searchFilter,
    setSearchFilter,
    showFilterOption: false,
    showLoadMore: false
  }

  return (
    <>
      {gettingListOfEmails && (
        <div className='flex flex-col gap-6 mt-8'>
          <Skeleton height={100} />
        </div>
      )}
      {!gettingListOfEmails && (
        <div className='font-openSans flex flex-col p-px w-full bg-white rounded overflow-y-auto'>
          {!listOfEmails?.length && (
            <div className='font-openSans flex flex-col justify-center items-center h-96 w-full bg-white mt-8 mb-8 rounded'>
              <MailIcon />
              <div className='font-semibold mb-2 mt-4'>
                You don't have any emails yet
              </div>
              <div className='text-sm text-softBlack70'>
                {`Send your first email to ${contact?.firstName ?? '...'}`}
              </div>
              <button
                className='bg-yellow-200 font-semibold px-4 py-2 rounded mt-6'
                onClick={() => {
                  setShowEmailBox(true)
                }}
              >
                Create Email
              </button>
            </div>
          )}
          {listOfEmails?.length && <EmailsCard childProps={childProps} />}
          {showEmailBox && (
            <EmailModal
              show={showEmailBox}
              handleClose={() => setShowEmailBox(!showEmailBox)}
              contact={contact}
              onEmailSent={() => {
                console.log('Email Sent successfully, fetching new mails')
                getEmails()
              }}
            />
          )}
        </div>
      )}
    </>
  )
}

export default EmailTab
